import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch, getToken, setToken, clearToken, isTokenExpired, API_BASE_URL } from '../dppApi';
import '../App.css';

// Recipient lands here after scanning/following a transfer link.
// Viewing the transfer requires login: the recipient logs in/registers first,
// then the preview loads (authenticated) and they can claim.
const ClaimTransfer = () => {
    const { tokenId } = useParams();
    const navigate = useNavigate();
    // An expired session here must fall back to this page's OWN login form, not a
    // redirect — bouncing them to /user would lose the claim link they followed.
    const [token, setUserToken] = useState(() => {
        const t = getToken('user');
        if (t && isTokenExpired(t)) { clearToken('user'); return null; }
        return t;
    });
    const [info, setInfo] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [claimed, setClaimed] = useState(false);
    const [claimError, setClaimError] = useState(null);
    const [authMode, setAuthMode] = useState('login');
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [busy, setBusy] = useState(false);

    const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    // Load the preview only once the viewer is authenticated. The token_id
    // alone no longer grants a preview — the backend requires a user token too.
    useEffect(() => {
        if (!token) return;
        fetch(`${API_BASE_URL}/api/transfer/token/${tokenId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => {
                if (data.error) setLoadError(data.error);
                else setInfo(data);
            })
            .catch(() => setLoadError('Failed to load transfer details'));
    }, [tokenId, token]);

    const handleClaim = async () => {
        setBusy(true);
        setClaimError(null);
        try {
            await apiFetch(`/api/transfer/claim/${tokenId}`, { method: 'POST', token });
            setClaimed(true);
        } catch (err) {
            setClaimError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setBusy(true);
        setClaimError(null);
        try {
            const path = authMode === 'login' ? '/api/user/login' : '/api/user/register';
            const body = authMode === 'login'
                ? { email: form.email, password: form.password }
                : form;
            const data = await apiFetch(path, { method: 'POST', body });
            setToken('user', data.token);
            setUserToken(data.token);   // triggers the authenticated preview fetch
        } catch (err) {
            setClaimError(err.message);
        } finally {
            setBusy(false);
        }
    };

    // ── Not logged in: gate the whole view behind auth ──────────────────────────
    if (!token) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <h2 className="dpp-auth-title">Accept transfer</h2>
                <form onSubmit={handleAuth}>
                    <p className="dpp-muted" style={{ margin: '0 0 8px' }}>
                        {authMode === 'login' ? 'Log in' : 'Sign up'} to view and claim this product.
                    </p>
                    {authMode === 'register' && (
                        <input className="dpp-input" placeholder="Name" value={form.name}
                            onChange={update('name')} />
                    )}
                    <input className="dpp-input" type="email" placeholder="Email"
                        value={form.email} onChange={update('email')} required />
                    <input className="dpp-input" type="password" placeholder="Password"
                        value={form.password} onChange={update('password')} required />
                    {claimError && <p className="dpp-error">{claimError}</p>}
                    <button className="dpp-button" type="submit" disabled={busy}>
                        {busy ? 'Please wait…' : (authMode === 'login' ? 'Log in' : 'Sign up')}
                    </button>
                    <button type="button" className="dpp-link-button"
                        onClick={() => { setClaimError(null); setAuthMode(authMode === 'login' ? 'register' : 'login'); }}>
                        {authMode === 'login' ? 'Need an account? Sign up' : 'Have an account? Log in'}
                    </button>
                </form>
            </div>
        </div>
    );

    if (loadError) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <p className="dpp-error">{loadError}</p>
            </div>
        </div>
    );

    if (!info) return (
        <div className="dpp-auth-page">
            <p className="dpp-muted">Loading…</p>
        </div>
    );

    if (!info.is_valid) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <p className="dpp-error">This transfer link has already been claimed or has expired.</p>
            </div>
        </div>
    );

    if (claimed) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <h2 className="dpp-auth-title">Transfer complete</h2>
                <p><strong>{info.product_name}</strong> is now in your collection.</p>
                <button className="dpp-button" onClick={() => navigate('/user/collection')}>View collection</button>
            </div>
        </div>
    );

    // ── Logged in: preview + claim ──────────────────────────────────────────────
    return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <h2 className="dpp-auth-title">Accept transfer</h2>
                {info.image_url && info.image_url.startsWith('http') && (
                    <img
                        src={info.image_url}
                        alt={info.product_name}
                        style={{ width: '100%', borderRadius: 8, marginBottom: 8, objectFit: 'cover' }}
                    />
                )}
                <p style={{ margin: '4px 0' }}><strong>{info.product_name}</strong></p>
                <p className="dpp-muted" style={{ margin: '0 0 12px' }}>
                    Transfer type: {info.transfer_type}
                </p>

                {claimError && <p className="dpp-error">{claimError}</p>}

                <button className="dpp-button" onClick={handleClaim} disabled={busy}>
                    {busy ? 'Claiming…' : 'Claim this product'}
                </button>
            </div>
        </div>
    );
};

export default ClaimTransfer;
